import React, { useState } from 'react';
import { campaignsAPI } from '../../../../services/api';
import './Overview.css';

function Overview({ campaign, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    name: campaign?.name || '',
    description: campaign?.description || '',
    startingXp: campaign?.startingXp || 1000,
    locations: campaign?.locations || '',
    lastSessionRecap: campaign?.lastSessionRecap || '',
    notes: campaign?.notes || ''
  });

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const payload = {
        name: form.name,
        description: form.description,
        startingXp: Number(form.startingXp),
        locations: form.locations,
        lastSessionRecap: form.lastSessionRecap,
        notes: form.notes
      };

      const res = await campaignsAPI.updateCampaign(campaign.id, payload);
      const updated = res.data?.campaign;
      
      if (updated) {
        onUpdate(updated);
        setIsEditing(false);
      }
    } catch (e) {
      console.error('Failed to save campaign:', e);
      setError(e?.response?.data?.error || 'Failed to save campaign');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      name: campaign?.name || '',
      description: campaign?.description || '',
      startingXp: campaign?.startingXp || 1000,
      locations: campaign?.locations || '',
      lastSessionRecap: campaign?.lastSessionRecap || '',
      notes: campaign?.notes || ''
    });
    setIsEditing(false);
    setError(null);
  };

  return (
    <div className="overview-section">
      <div className="section-header">
        <h2>Campaign Overview</h2>
        <div className="section-actions">
          {!isEditing ? (
            <button className="btn-primary" onClick={() => setIsEditing(true)}>
              Edit Overview
            </button>
          ) : (
            <>
              <button className="btn-secondary" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="overview-content">
        <div className="overview-grid">
          {/* Basic Information Card */}
          <div className="overview-card">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label>Campaign Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  disabled={isSaving}
                  placeholder="Enter campaign name"
                />
              ) : (
                <div className="display-value">{campaign?.name || 'Untitled Campaign'}</div>
              )}
            </div>

            <div className="form-group">
              <label>Description</label>
              {isEditing ? (
                <textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  disabled={isSaving}
                  rows={4}
                  placeholder="Brief campaign description..."
                />
              ) : (
                <div className="display-value multiline">
                  {campaign?.description || 'No description provided'}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Starting XP for New Characters</label>
              {isEditing ? (
                <input
                  type="number"
                  min={0}
                  step={50}
                  value={form.startingXp}
                  onChange={(e) => setForm(prev => ({ ...prev, startingXp: e.target.value }))}
                  disabled={isSaving}
                />
              ) : (
                <div className="display-value">{campaign?.startingXp || 1000} XP</div>
              )}
              <span className="hint">Default: 1000 XP</span>
            </div>
          </div>

          {/* Locations Card */}
          <div className="overview-card">
            <h3>Key Locations</h3>
            
            <div className="form-group">
              {isEditing ? (
                <textarea
                  value={form.locations}
                  onChange={(e) => setForm(prev => ({ ...prev, locations: e.target.value }))}
                  disabled={isSaving}
                  rows={8}
                  placeholder="List important locations in your campaign world...

Examples:
• Charl Town - The ruined city where the war ended
• New Haven - A Church-controlled settlement
• The Wastes - Irradiated badlands"
                />
              ) : (
                <div className="display-value multiline locations">
                  {campaign?.locations || 'No locations added yet'}
                </div>
              )}
            </div>
          </div>

          {/* Last Session Recap Card */}
          <div className="overview-card full-width">
            <h3>Last Session Recap</h3>
            
            <div className="form-group">
              {isEditing ? (
                <textarea
                  value={form.lastSessionRecap}
                  onChange={(e) => setForm(prev => ({ ...prev, lastSessionRecap: e.target.value }))}
                  disabled={isSaving}
                  rows={6}
                  placeholder="What happened in the last session? Major events, decisions made, cliffhangers..."
                />
              ) : (
                <div className="display-value multiline">
                  {campaign?.lastSessionRecap || 'No session recap yet'}
                </div>
              )}
            </div>
          </div>

          {/* GM Notes Card */}
          <div className="overview-card full-width">
            <h3>GM Notes</h3>
            
            <div className="form-group">
              {isEditing ? (
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  disabled={isSaving}
                  rows={6}
                  placeholder="Private notes for your eyes only. Plot hooks, reminders, ideas..."
                />
              ) : (
                <div className="display-value multiline">
                  {campaign?.notes || 'No notes yet'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Overview;
